import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";

import * as moment from 'moment';

import { SessionManager } from "./session-manager";

import { GiviScanSessionModel, SessionModel } from '@models/SessionModel';
import { CustomerModel } from '@models/CustomerModel';
import { TransactionModel } from "@models/TransactionModel";
import { Country, PointTransactionType } from "@models/Enums";

import { SharedService } from './shared.service';
import { envVariables } from '@shared/mock-data';

import { environment } from '@environments/environment';


@Injectable()
export class ApiBaseService {
    // FOR DEPLOY
    // Commands: Dev CR: ng build
    //           Prod CR: ng build --prod
    baseUrl: string = environment.baseUrl;
    siteUrl: string = environment.siteUrl;

    // For Debug
    // Commands: Dev CR: ng serve
    // baseUrl = 'https://localhost:63312';
    // siteUrl = 'http://localhost:4200';
    // baseUrl = 'https://10.211.55.7:45455'; // conveyor.cloud

    // baseUrl = 'https://api.giviapp.com'; // Prod

    // baseUrl = 'https://apidemo.giviapp.com'; // Demo
    // siteUrl = 'https://ewallet.giviapp.com'; //Demo

    // Temp cache, used to access token
    private session: SessionModel

    constructor(public http: HttpClient, public sharedService: SharedService) {
        if (!envVariables.initialized) this.getEnvVariables();
    }

    getEnvVariables(): Promise<any> {

        // Implementation using Promise
        return this.http.post(`${this.baseUrl}/api/admin/market/env/reads/v1`, null, this.getHttpOptions())
            .toPromise().then(
                (variables: any) => {

                    // Clean and set the variables to cache
                    variables.forEach(x => {
                        envVariables[x.key] = x.value;
                    });

                    envVariables.initialized = true;

                    return envVariables;
                },
                (err: any) => {

                    envVariables.initialized = false;

                    const errMsg = this.getErrorMsg(err);

                    return Promise.reject(new Error(errMsg));
                }
            );
    }
    

    setSession(session: SessionModel) {

        this.session = session;
    }

    // Get session if found any
    getSession(): SessionModel {

        const currSession = this.session ?? SessionManager.getSession(true);

        // first we validate if current session is still valid, otherwise look for it in storage
        if (this.isValidSession(currSession)) {
            return currSession;
        }
        return null;
    }

    // Get session if found any
    getGiviScanSession(): GiviScanSessionModel {

        const currSession = SessionManager.getSession(true);

        // first we validate if current session is still valid, otherwise look for it in storage
        if (this.isValidSession(currSession?.giviScanSession)) {
            return currSession.giviScanSession;
        }
        return null;
    }

    // verify is session is valid
    isValidSession(session: SessionModel | GiviScanSessionModel): boolean {

        if (session && session.accessToken) {
            return true;
        }
        return false;
    }

    // GET /api/admin/account/reads/v1/{id}
    loadAccount(ignoreErrorAlert: boolean = false): Promise<CustomerModel> {
        const session = this.getSession();
        // check if exist the company into session
        if (!(session && session.user && session.user.id)) {
            return Promise.reject(null);
        }

        this.refreshSession();

        const id = session.user.id;
        const country = session.user.country;

        if (id == -1) {

            return Promise.resolve(new CustomerModel(session.user));
        }

        return this.http.get(
            `${this.baseUrl}/api/admin/account/reads/v1/${id}`,
            this.getHttpOptions()
        )
            .toPromise()
            .then(
                (user: any) => {

                    const customer = new CustomerModel(user || session.user);
                    customer.country = country;

                    SessionManager.updateSessionAccount(customer);
                    this.sharedService.setAccount(customer);

                    return customer;
                },
                (err: HttpErrorResponse) => {
                    if (ignoreErrorAlert) {
                        return Promise.reject(err);
                    }
                    // Unauthorized
                    if (err.status == 401 && err.error) {
                        this.sharedService.showSessionTimeoutAlert(err.error);
                    }
                    // NotFound
                    else if (err.status == 404) {
                        this.sharedService.showSessionTimeoutAlert(
                            "Error al cargar información de la cuenta, debe iniciar sesión nuevamente"
                        );
                    }

                    const errMsg = this.getErrorMsg(err, null, true);

                    return Promise.reject(
                        new Error(
                            `Error al cargar información de la cuenta: ${errMsg}`
                        )
                    );
                }
            );
    }

    logout(router?: Router, goToLogin = false): Promise<any> {
        // clear session and remove from storage
        this.setSession(null);
        SessionManager.clearSession();
        if (router) {
            if (goToLogin) {
                router.navigate(["login"]);
            } else {
                // navigate to logout when logout success
                router.navigate(["/logout"], {
                    queryParams: { logout: "success" },
                });
            }
        }

        return Promise.resolve({ result: true });
    }

    // POST /token
    refreshSession(): Promise<void> {
        this.setSession(null);
        const session = this.getSession();
        // check if exist the company into session
        if (!(session && session.refreshToken)) {
            return Promise.reject(null);
        }

        const httpOpts = {
            headers: new HttpHeaders({
                "Cache-Control": "no-cache",
                "Content-Type": "application/x-www-form-urlencoded",
                "Access-Control-Allow-Headers": "Content-Type",
                "Access-Control-Allow-Methods":
                    "GET,POST,UPDATE,DELETE,OPTIONS",
                "Access-Control-Allow-Origin": "*",
            }),
        };

        const credentials = `grant_type=refresh_token&refresh_token=${session.refreshToken}`;

        return this.http.post(`${this.baseUrl}/token`, credentials, httpOpts)
            .toPromise()
            .then((response: any) => {
                SessionManager.updateSessionTokens(
                    response.access_token,
                    response.refresh_token
                );

                return Promise.resolve();
            });
    }

    logToWebHook(method: string, data: any): Promise<any> {
        const httpOpts = {
            headers: new HttpHeaders({
                "Cache-Control": "no-cache",
                "Content-Type": "application/json, text/plain, */*", // DEFAULT
                "Access-Control-Allow-Headers": "Content-Type",
                "Access-Control-Allow-Methods": "GET,POST",
                "Access-Control-Allow-Origin": "*",
            }),
        };

        return this.http.post(
            `https://webhook-test.com/a01aa7eec1b430b86cb52a96d1c1abd2`,
            data,
            httpOpts
        ).toPromise();
    }

    getServerTime(): Promise<Date> {
        return this.http.get(`${this.baseUrl}/home/currenttime`, this.getHttpOptions())
            .toPromise()
            .then(
                (result: any) => {
                    const now = new Date(result);
                    return now;
                },
                (err: HttpErrorResponse) => {
                    const errMsg = this.getErrorMsg(err, null, true);

                    return Promise.reject(
                        new Error(`Error al cargar la fecha y hora: ${errMsg}`)
                    );
                }
            );
    }

    
    // POST: api/admin/transactions/customer/purchases/reads/v1/{id}/{count?}
    recentPurchases(customerId: number, count: number): Promise<TransactionModel[]> {

        return this.http.post(`${this.baseUrl}/api/admin/transactions/customer/purchases/reads/v1/${customerId}/${count}`, null, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    const recentPurchases = result.map(x => {

                        return new TransactionModel(x);
                    });

                    return recentPurchases;
                }
            );
    }

    getRecentPurchaseGiftcards(count = 15, hours = 24): Promise<any> {

        const session = this.getSession();
        // check if exist the company into session
        if (!(session && session.user && session.user.id)) {
            return Promise.reject(null);
        }

        return this.http.post(`${this.baseUrl}/api/admin/transactions/customer/purchases/reads/v1/${session.user.id}/${count}`, null, this.getHttpOptions())
            .toPromise().then(
                (result: any) => {

                    const purchases = result.map(x => new TransactionModel(x));

                    // Filtra las transacciones que fueron compradas en las últimas 24 horas
                    let recentPurchases = purchases.filter(transaction => {
                        // Compras de POP en las ultimas 24 horas
                        if (transaction.type == PointTransactionType.PurchaseGiftcards) {
                            let isToday = moment().diff(moment(transaction.paymentAt), 'hours') < hours;
                            return isToday;
                        }
                        return false;
                    });

                    // Actualiza la lista de compras recientes a nivel de la session
                    SessionManager.setRecentPurchases(recentPurchases);

                    return recentPurchases;
                }
            );
    }

    getBitLyUrl(url: string): Promise<string> {

        return this.http.get(`${url}+`).toPromise()
            .then((result: any) => {

                return result;
            });
    }


    getHttpOptions(country?: Country): any {

        const session = this.getSession();
        const isDemoMode = SessionManager.isDemoMode();

        let selectedCountry = country != null ? country : session?.user?.country;

        const headers = this._getHeaders(selectedCountry, session?.accessToken, isDemoMode);
        const httpOpts = { headers: new HttpHeaders(headers) };

        return httpOpts;
    }

    getHttpOptionsWithoutSession(country): any {

        const isDemoMode = SessionManager.isDemoMode();

        const headers = this._getHeadersWithoutSession(country, isDemoMode);
        const httpOpts = { headers: new HttpHeaders(headers) };

        return httpOpts;
    }

    getHttpOptionsGSA(country?: Country): any {

        const session = this.getGiviScanSession();
        const isDemoMode = SessionManager.isDemoMode();

        let selectedCountry = country != null ? country : session?.user?.country;

        const headers = this._getHeaders(selectedCountry, session?.accessToken, isDemoMode);
        const httpOpts = { headers: new HttpHeaders(headers) };

        return httpOpts;
    }

    // Verify if error is string or has an error message
    // Return an error string
    getErrorMsg(resError: any, defaultErrText?: string, showSessionAlert?: boolean): string {
        let errMsg: string = null;
        if (resError) {
            if (typeof resError == "string" && resError.length) {
                errMsg = resError;
            } else if (resError.status == 0 && resError.statusText &&
                resError.statusText.indexOf("Unknown Error") >= 0) {
                errMsg = "Error relacionado con la conexión con el servidor, por favor compruebe su conexión a internet";
                return errMsg;
            } else if (resError.status == 400 && resError.error) {
                if (resError.error.Message) {
                    errMsg = resError.error.Message;
                }
                else if (resError.error.error_description) {
                    errMsg = resError.error.error_description;
                }
                else if (resError.error.error == "invalid_grant") {
                    errMsg = "El usuario o contraseña son inválidos, por favor revisá la información y volvé a intentarlo.";
                    return errMsg;
                }
                else if (resError.error.error == "connecting" || resError.error.error == "Server_internalError") {
                    errMsg = "Error relacionado con la red o una conexión con el servidor, por favor compruebe su conexión a internet";
                    return errMsg;
                }
                else if (typeof resError.error == "string" && resError.error.length) {
                    errMsg = resError.error;
                }
            } else if (resError.status == 401) {
                if (typeof resError.error == "string" && resError.error.length) {
                    errMsg = resError.error;

                    if (resError.error == "Unauthorized") {
                        errMsg = "La sesión ha caducado.";

                        this.sharedService.showSessionTimeoutAlert();
                        return errMsg;
                    }
                } else if (
                    (resError.error?.message && resError.error?.message.indexOf("Authorization has been denied") >= 0) ||
                    (resError.error?.Message && resError.error?.Message.indexOf("Authorization has been denied") >= 0) ||
                    resError.statusText == "Unauthorized") {
                    errMsg = "La sesión ha caducado.";

                    this.sharedService.showSessionTimeoutAlert();
                    return errMsg;
                } else if (resError.message) {
                    errMsg = resError.message;
                } else if (resError.Message) {
                    errMsg = resError.Message;
                } else {
                    errMsg = "La sesión ha caducado.";

                    this.sharedService.showSessionTimeoutAlert();
                    return errMsg;
                }
            } else if (resError.status == 400 && resError.error && typeof resError.error == "string") {
                if (resError.error.indexOf("no está registrado") >= 0) {
                    errMsg = resError.error;
                    return errMsg;
                } else if (resError.error.indexOf("ya existe") >= 0) {
                    errMsg = "Ya existe una cuenta con este email. Por favor regresá a Iniciar Sesión, y en caso de haberla olvidado podés proceder a recuperar tu contraseña.";
                    return errMsg;
                } else if (resError.error.indexOf("es inválido") >= 0) {
                    errMsg = resError.error;
                    return errMsg;
                } else if (resError.error.length) {
                    errMsg = resError.error;
                } else if (resError.message) {
                    errMsg = resError.message;
                } else if (resError.Message) {
                    errMsg = resError.Message;
                }
            } else if (resError.error && resError.error.error_description) {
                errMsg = resError.error.error_description;
            } else if (resError.error && resError.error.message) {
                errMsg = resError.error.message;
            } else if (resError.error && resError.error.Message) {
                errMsg = resError.error.Message;
            } else if (resError.message) {
                errMsg = resError.message;
            }
            // Custom errors
            if (errMsg) {
                if (errMsg.indexOf("Http failure response") >= 0) {
                    errMsg =
                        "El servidor no proceso la información correctamente, por favor compruebe su conexión a internet y volvé a intentarlo.";
                }
                if (errMsg.indexOf("unknown url") >= 0) {
                    errMsg = "No se pudo establecer conexión con el servidor, por favor compruebe su conexión a internet y volvé a intentarlo.";
                }
                if (errMsg.indexOf("AdminDbContext") >= 0) {
                    errMsg = "El servidor no se encuentra disponible, por favor intente en otro momento.";
                }
                if (errMsg.indexOf("EntityValidationErrors") >= 0) {
                    errMsg = "El servidor no proceso la información correctamente, por favor revisá la información y volvé a intentarlo.";
                }
                if (errMsg.indexOf("updating the entries") >= 0) {
                    errMsg = "El servidor no proceso la información correctamente, por favor revisá la información y volvé a intentarlo.";
                }
                if (errMsg.indexOf("The underlying provider failed on Open") >= 0) {
                    errMsg = "El servidor no proceso la información correctamente, por favor volvé a intentarlo.";
                }
            }
        }
        // If not err message, return default error
        if (!errMsg && defaultErrText) {
            return defaultErrText;
        }

        errMsg = errMsg || "Error desconocido.";
        return errMsg;
    }

    /**
     * Generate a token based on the giviId, current time and current token timer value.
     * The token is generated based on the last digit of the current minutes and the giviId.
     * @param giviId - The GIVI ID to use for generating the token.
     * @param currentTokenTimerMax - The maximum value for the current token timer (default is 1800 seconds 5 minutes).
     * @returns An object containing the generated token and the current token timer value.
     */
    getToken(giviId: number, currentTokenTimerMax = 1800): { token: string, currentTokenTimerValue: number } {

        const now = new Date();
        now.setMilliseconds(0);

        // Get the last digit of the current minutes
        const lastMinute = parseInt(now.getMinutes().toString().padStart(2, '0').slice(1), 10);

        // Adjust the last minute to 0, 5
        if (lastMinute < 5) {

            now.setMinutes(now.getMinutes() - lastMinute, 0, 0);
        } else if (lastMinute > 5) {

            now.setMinutes(now.getMinutes() - (lastMinute - 5), 0, 0);
        }

        // Get the current hours
        let hours = now.getHours().toString().padStart(2, '0');
        if (hours == '00') {
            // Adjust the O hours to 24
            hours = '24';
        }

        // Get the adjusted minutes
        const minutes = now.getMinutes().toString().padStart(2, '0');


        // Calcular el timer
        const currentNow = new Date();
        const currentMinutesStr = currentNow.getMinutes().toString().padStart(2, '0');
        const currentSecondsStr = currentNow.getSeconds().toString().padStart(2, '0');
        const currentSeconds = (parseInt(currentMinutesStr, 10) * 60) + (parseInt(currentSecondsStr, 10));

        const minutesDiff = (currentSeconds - (parseInt(minutes, 10) * 60)) * 60;
        const currentTokenTimerValue = currentTokenTimerMax - minutesDiff;


        // Create the code according to givi id, hours and minutes
        const lastGiviId = giviId.toString().padStart(2, '0').slice(1);
        const timeCode = parseInt(lastGiviId + hours + minutes, 10);

        // Hash the code for convert to token
        const tokenHash = Math.floor(timeCode * 1.5);
        let token = tokenHash.toString().padStart(5, "0");
        if (token.length > 5) {
            token = token.substring(token.length - 5, token.length);
        }

        return { token, currentTokenTimerValue };
    }


    private _getHeaders(country: Country, authenticationToken?: string, isDemoMode = false) {

        const headers = {
            "Cache-Control": "no-cache",
            "Content-Type": "application/json, text/plain, */*", // DEFAULT
            "Access-Control-Allow-Headers": "Content-Type",
            "Access-Control-Allow-Methods": "GET,POST,UPDATE,DELETE,OPTIONS",
            "Access-Control-Allow-Origin": "*"
        };
        if (country) {
            headers['X-COUNTRY'] = country;
        }
        if (isDemoMode) {
            headers['X-TESTMODE'] = '1';
        }
        if (authenticationToken) {
            headers["Authorization"] = `Bearer ${authenticationToken}`;
        }
        return headers;
    }

    private _getHeadersWithoutSession(country: Country, isDemoMode = false) {

        return this._getHeaders(country, null, isDemoMode);
    }
}
